import React, { useCallback, useState } from 'react'
import ZoomPanDraggable from '../ZoomPanDraggable/ZoomPanDraggable'
import { useMemoryBook } from '../../providers/MemoryBookProvider'

const BaseFreeformPicker = ({
  picker,
  onPositionCommit,
  onRotateCommit,
  onDelete,
  onActivate,
  stickerBoundsRef,
  pageOffset,
  isPrintView = false,
  zindex,
  renderContent,
  resizeMode = 'freeform',
  maxSize = 600,
  minSize = 50,
  defaultSize = 300,
  contentDescription,
  deleteButtonTitle,
  testIdPrefix,
  onAfterSave,
  shouldWaitForContentReady = false,
}) => {
  const { updateCustomLayoutElementData } = useMemoryBook()
  const [isProcessingContent, setIsProcessingContent] = useState(false)

  // Handler to update this element's picker data
  const handleUpdateElementData = useCallback(
    (updates) => {
      updateCustomLayoutElementData(picker.pageNumber, picker.id, updates)

      if (shouldWaitForContentReady) {
        setIsProcessingContent(true)
      }

      if (onAfterSave) {
        onAfterSave()
      }
    },
    [updateCustomLayoutElementData, picker.pageNumber, picker.id, shouldWaitForContentReady, onAfterSave]
  )

  const handleContentReady = useCallback(() => {
    if (isProcessingContent) {
      setIsProcessingContent(false)
    }
  }, [isProcessingContent])

  const initialPosition = {
    x: picker.position?.x ?? 0,
    y: picker.position?.y ?? 0,
    width: picker.size?.width ?? defaultSize,
    height: picker.size?.height ?? defaultSize,
  }

  // Common content props passed to renderContent
  const contentProps = {
    pageNumber: picker.pageNumber,
    pickerData: picker.pickerData || {},
    isPrintView,
    customLayoutElementId: picker.id,
    onUpdateCustomLayoutElementData: handleUpdateElementData,
    onContentReady: shouldWaitForContentReady && isProcessingContent ? handleContentReady : undefined,
    dimensions: { width: initialPosition.width, height: initialPosition.height },
  }

  // Render print view - static positioned div
  if (isPrintView) {
    const PRINT_POSITION_SCALE_X = 1.35
    const PRINT_POSITION_SCALE_Y = 1.27
    const PRINT_SIZE_SCALE = 1.3

    const finalX = initialPosition.x * PRINT_POSITION_SCALE_X
    const finalY = initialPosition.y * PRINT_POSITION_SCALE_Y
    const finalWidth = initialPosition.width * PRINT_SIZE_SCALE
    const finalHeight = initialPosition.height * PRINT_SIZE_SCALE

    return (
      <div
        style={{
          position: 'absolute',
          insetInlineStart: `${finalX}px`,
          top: `${finalY}px`,
          width: `${finalWidth}px`,
          height: `${finalHeight}px`,
          transform: `rotate(${picker.rotation || 0}deg)`,
        }}
      >
        {renderContent(contentProps)}
      </div>
    )
  }

  // Render layout mode - full ZoomPanDraggable wrapper
  const resizeProps = {}
  if (resizeMode === 'freeform') {
    resizeProps.resizeFreeform = true
  } else if (resizeMode === 'corners') {
    resizeProps.resizeCorners = true
  } else if (resizeMode === 'horizontalOnly') {
    resizeProps.resizeHorizontalOnly = true
  }

  return (
    <ZoomPanDraggable
      initialPosition={initialPosition}
      onActivate={onActivate}
      onDelete={onDelete}
      onPositionCommit={onPositionCommit}
      onRotateCommit={onRotateCommit}
      rotation={picker.rotation || 0}
      stickerBoundsRef={stickerBoundsRef}
      pageOffset={pageOffset}
      style={{
        zIndex: zindex,
      }}
      testIdPrefix={testIdPrefix}
      defaultSize={defaultSize}
      contentDescription={contentDescription}
      deleteButtonTitle={deleteButtonTitle}
      maxSize={maxSize}
      minSize={minSize}
      {...resizeProps}
    >
      {() => renderContent(contentProps)}
    </ZoomPanDraggable>
  )
}

export default BaseFreeformPicker
